import { Rocket } from '@griply/assets';
import Typography from '@mui/material/Typography';
import {
  useSubscriptionCards,
  useUserHadTrial,
  useUserHasPremium,
} from 'features/user';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import { SideNavigationItem } from 'shared/components/ui/side-navigation-item';
import { version } from 'shared/constants';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';

import { settingsNavGroups, SettingsSection } from './settings-nav';
import * as Styled from './settings-navigation.style';

const NAV_ICON_SIZE = 16;
const NAV_ICON_STROKE_WIDTH = 1.5;

export type SettingsNavigationProps = {
  activeSection: SettingsSection;
  onSelect: (section: SettingsSection) => void;
};

export const SettingsNavigation: React.FC<SettingsNavigationProps> = ({
  activeSection,
  onSelect,
}) => {
  const { t } = useTranslation();
  const openPremiumDialog = useOpenPremiumDialog();
  const userHasPremium = useUserHasPremium();
  const userHadTrial = useUserHadTrial();
  // A card exists for every past subscription too, so an expired one keeps the panel reachable.
  const hasSubscriptions = !!useSubscriptionCards().length;

  const navGroups = useMemo(() => {
    if (hasSubscriptions) {
      return settingsNavGroups;
    }

    return settingsNavGroups.map((group) => ({
      ...group,
      items: group.items.filter(
        ({ section }) => section !== SettingsSection.Subscription,
      ),
    }));
  }, [hasSubscriptions]);

  return (
    <Styled.Sidebar>
      <Styled.TitleContainer>
        <Typography variant="h5" component="h1">
          {t('forms.user-settings.title')}
        </Typography>
      </Styled.TitleContainer>

      <Styled.Nav>
        {!userHasPremium && (
          <Styled.Group>
            <SideNavigationItem
              label={t(
                userHadTrial
                  ? 'general.labels.premium.upgrade'
                  : 'general.labels.premium.free',
              )}
              start={
                <Icon
                  icon={Rocket}
                  size={NAV_ICON_SIZE}
                  strokeWidth={NAV_ICON_STROKE_WIDTH}
                />
              }
              onClick={openPremiumDialog}
              prominent
            />
          </Styled.Group>
        )}

        {navGroups.map((group, index) => (
          <Styled.Group key={group.translationLabel ?? `group-${index}`}>
            {!!group.translationLabel && (
              <Styled.GroupHeader>
                {t(group.translationLabel)}
              </Styled.GroupHeader>
            )}

            {group.items.map((item) => {
              const handleSelect = () => onSelect(item.section);

              return (
                <SideNavigationItem
                  key={item.section}
                  label={t(item.translationLabel)}
                  start={
                    <Icon
                      icon={item.icon}
                      size={NAV_ICON_SIZE}
                      strokeWidth={NAV_ICON_STROKE_WIDTH}
                    />
                  }
                  active={item.section === activeSection}
                  onClick={handleSelect}
                />
              );
            })}
          </Styled.Group>
        ))}
      </Styled.Nav>

      <Styled.Footer>
        <Styled.Version variant="overline">{version}</Styled.Version>
      </Styled.Footer>
    </Styled.Sidebar>
  );
};
